import React, { useEffect, useState } from "react";
import axios from "axios";
import { cachedGet } from "./apiCache.js";
import { useModuleVisibility } from "./useModuleVisibility.js";

const MODULE_NAME = "_61_multi_day_trades";

function Tooltip({ text }) {
    return (
        <span style={{ marginLeft: 4, verticalAlign: "middle", cursor: "pointer", color: "#888" }} title={text}>🛈</span>
    );
}

function formatK(val) {
    if (val === null || val === undefined || isNaN(val)) return "";
    const abs = Math.abs(val);
    if (abs >= 1e6) return (val / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    if (abs >= 1e3) return (val / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Helper to parse date strings (MM/DD/YY or YYYY-MM-DD)
function parseDate(d) {
    if (!d) return 0;
    const mdy = d.match(/^(\d{2})\/(\d{2})\/(\d{2,4})$/);
    if (mdy) {
        const [_, mm, dd, yy] = mdy;
        const yyyy = yy.length === 2 ? "20" + yy : yy;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime();
    }
    const ymd = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
        const [_, yyyy, mm, dd] = ymd;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime();
    }
    return new Date(d).getTime();
}

function MultiDayTradesTable(props) {
    // DECLARE ALL HOOKS FIRST - Before any conditional logic
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [collapsed, setCollapsed] = useState(false);
    const [sortColumn, setSortColumn] = useState("initial_transaction_date");
    const [sortDirection, setSortDirection] = useState("asc");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError("");

        // 🔓 Retrieve userKey from sessionStorage for decryption
        const userKey = sessionStorage.getItem("userKey");

        const params = {
            navigationDate: props.navigationDate,
            refreshKey: props.refreshKey,
        };
        if (userKey) {
            params.userKey = userKey;
        }

        axios.get(`${process.env.REACT_APP_API_URL}/api/multi-day-trades`, {
            params,
            withCredentials: true,
        })
            .then(res => {
                setLoading(false);
                setError("");
                setData(res.data);
            })
            .catch(err => {
                setLoading(false);
                setError("Could not fetch multi-day trades");
                setData(null);
            });
    }, [props.navigationDate, props.refreshKey]);

    // Check visibility based on module_name URL parameter - AFTER all other hooks
    const isVisible = useModuleVisibility(MODULE_NAME);

    // Return null if component is not visible
    if (!isVisible) return null;

    // --- FIX: Only destructure if data is not null/undefined and has the expected keys ---
    const multi_day_trades = data?.multi_day_trades || [];
    const totals = data?.totals || {};
    const tooltip = data?.tooltip || "";

    function openStatusCell(status) {
        if (status === "yes") {
            return (
                <span style={{ display: "inline-block", background: "orange", color: "#fff", borderRadius: "12px", padding: "2px 12px", fontWeight: 700, fontSize: 13, boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>OPEN</span>
            );
        }
        return <span style={{ color: "#888" }}>No</span>;
    }

    function colorPL(val) {
        if (val === null || val === undefined || isNaN(val)) return "";
        const color = val > 0 ? "#228B22" : val < 0 ? "#B22222" : "#fff";
        return <span style={{ color, fontWeight: 700 }}>${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
    }

    // Handle column header click for sorting
    const handleColumnSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    // Render sort indicator
    const getSortIndicator = (column) => {
        if (sortColumn !== column) return " ⇅";
        return sortDirection === "asc" ? " ↑" : " ↓";
    };

    // Sort trades based on column and direction
    const sortTrades = (trades) => {
        if (!trades || trades.length === 0) return trades;

        const sorted = [...trades].sort((a, b) => {
            let aVal, bVal;

            switch (sortColumn) {
                case "symbol":
                    aVal = (a.symbol || "").toUpperCase();
                    bVal = (b.symbol || "").toUpperCase();
                    break;
                case "max_qty":
                    aVal = Number(a.max_qty || 0);
                    bVal = Number(b.max_qty || 0);
                    break;
                case "max_qty_dollar":
                    aVal = Number(a.max_qty_dollar || 0);
                    bVal = Number(b.max_qty_dollar || 0);
                    break;
                case "max_qty_date":
                    aVal = parseDate(a.max_qty_date);
                    bVal = parseDate(b.max_qty_date);
                    break;
                case "num_trades":
                    aVal = Number(a.num_trades || 0);
                    bVal = Number(b.num_trades || 0);
                    break;
                case "num_days_open":
                    aVal = Number(a.num_days_open || 0);
                    bVal = Number(b.num_days_open || 0);
                    break;
                case "initial_transaction_date":
                    aVal = parseDate(a.initial_transaction_date);
                    bVal = parseDate(b.initial_transaction_date);
                    break;
                case "closing_transaction_date":
                    aVal = parseDate(a.closing_transaction_date);
                    bVal = parseDate(b.closing_transaction_date);
                    break;
                case "average_price":
                    aVal = Number(a.average_price || 0);
                    bVal = Number(b.average_price || 0);
                    break;
                case "open_status":
                    aVal = a.open_status === "yes" ? 1 : 0;
                    bVal = b.open_status === "yes" ? 1 : 0;
                    break;
                case "profit_loss":
                    aVal = Number(a.profit_loss || 0);
                    bVal = Number(b.profit_loss || 0);
                    break;
                default:
                    return 0;
            }

            if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
            if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });

        return sorted;
    };

    if (error) return <div style={{ color: "#e63946" }}>{error}</div>;
    if (loading) return <div style={{ color: "#aaa", padding: 16 }}>Loading...</div>;

    const sortedTrades = sortTrades(multi_day_trades);

    const headerStyle = {
        padding: "8px",
        fontSize: 13,
        textAlign: "left",
        borderBottom: "1px solid #333",
        color: "#888",
        cursor: "pointer",
        userSelect: "none"
    };

    const columns = [
        { key: "symbol", label: "Symbol" },
        { key: "max_qty", label: "Max Qty" },
        { key: "max_qty_dollar", label: "Max Qty $" },
        { key: "max_qty_date", label: "Max Qty Date" },
        { key: "num_days_open", label: "Days Open" },
        { key: "initial_transaction_date", label: "Open Date" },
        { key: "closing_transaction_date", label: "Close Date" },
        { key: "average_price", label: "Avg Price" },
        { key: "open_status", label: "Open?" },
        { key: "profit_loss", label: "P/L" }
    ];

    return (
        <div className={`collapse-toggle-container${collapsed ? " collapsed" : ""} multi-day-trades-table-container`} style={{ background: "#181A25", borderRadius: 8, paddingTop: 40, paddingRight: 20, paddingBottom: 32, paddingLeft: 20 }} data-analytics={MODULE_NAME}>
            <h4 className="collapse-toggle-headline" onClick={() => setCollapsed(!collapsed)}>
                📅 Multi-Day Stock Trades <Tooltip text={tooltip} />
            </h4>
            <div className="collapse-content">
                <table className="multi-day-trades-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    style={{
                                        ...headerStyle,
                                        backgroundColor: sortColumn === col.key ? "#333" : "transparent"
                                    }}
                                    onClick={() => handleColumnSort(col.key)}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#444"}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = sortColumn === col.key ? "#333" : "transparent"}
                                >
                                    {col.label}{getSortIndicator(col.key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedTrades && sortedTrades.map((row, i) => (
                            <tr key={i} className="multi-day-trade-row">
                                <td style={{ padding: "8px" }}>{row.symbol}</td>
                                <td style={{ padding: "8px" }}>{Number(row.max_qty || 0).toLocaleString()}</td>
                                <td style={{ padding: "8px" }}>{formatK(row.max_qty_dollar)}</td>
                                <td style={{ padding: "8px" }}>{row.max_qty_date}</td>
                                <td style={{ padding: "8px" }}>{row.num_days_open}</td>
                                <td style={{ padding: "8px" }}>{row.initial_transaction_date}</td>
                                <td style={{ padding: "8px", backgroundColor: "rgba(255, 255, 255, 0.05)", borderLeft: "1px solid #333", borderRight: "1px solid #333" }}>{row.closing_transaction_date || ""}</td>
                                <td style={{ padding: "8px" }}>${Number(row.average_price || 0).toFixed(2)}</td>
                                <td style={{ padding: "8px" }}>{openStatusCell(row.open_status)}</td>
                                <td style={{ padding: "8px" }}>{colorPL(row.profit_loss)}</td>
                            </tr>
                        ))}
                        {totals && (
                            <tr className="multi-day-trade-row total-row" style={{ borderTop: "2px solid #555", backgroundColor: "rgba(255,255,255,0.05)", fontWeight: "bold" }}>
                                <td style={{ padding: "10px 8px" }}>Total</td>
                                <td />
                                <td style={{ padding: "10px 8px" }}>{formatK(totals.max_qty_dollar)}</td>
                                <td colSpan={6} />
                                <td style={{ padding: "10px 8px" }}>{colorPL(totals.profit_loss)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default MultiDayTradesTable;